import React, { useEffect, useRef, useState } from 'react';
import { useHistoryPanelData } from '../../hooks/useHistoryPanelData';
import { HISTORY_COLUMN_NAMES } from '../order/OrderHistoryView';
import { shortUsername, formatRelativeDate } from './historyFormatting';

interface HistoryRecord {
  recno: number;
  dbtable: string;
  keyvalue: string;
  datetime: string;
  userid: string;
  colname: string;
  oldval: string | null;
  newval: string | null;
  type: string;
  otherid: string | null;
}

interface HistoryResponse {
  count: number;
  records: HistoryRecord[];
}

interface FieldHistoryPanelProps {
  proNumber: number | string;
  colnames: string[];
  title: string;
  anchor: { x: number; y: number };
  z: number;
  onClose: () => void;
}

const PANEL_WIDTH = 320;
const PANEL_MAX_HEIGHT = 360;
const MARGIN = 8;

function clamp(value: number, max: number) {
  return Math.max(MARGIN, Math.min(value, max));
}

export const FieldHistoryPanel: React.FC<FieldHistoryPanelProps> = ({
  proNumber,
  colnames,
  title,
  anchor,
  z,
  onClose,
}) => {
  const { data, loading, error } = useHistoryPanelData<HistoryResponse>(
    `/orders/history/${proNumber}?colnames=${colnames.join(',')}`,
    true,
    0,
    'Failed to load field history',
  );

  const [position, setPosition] = useState(() => ({
    top: clamp(anchor.y, window.innerHeight - PANEL_MAX_HEIGHT - MARGIN),
    left: clamp(anchor.x, window.innerWidth - PANEL_WIDTH - MARGIN),
  }));
  const dragRef = useRef<{ startX: number; startY: number; originTop: number; originLeft: number } | null>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDragStart = (event: React.PointerEvent) => {
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originTop: position.top,
      originLeft: position.left,
    };
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event: React.PointerEvent) => {
    if (!dragRef.current) return;
    const { startX, startY, originTop, originLeft } = dragRef.current;
    setPosition({
      top: originTop + (event.clientY - startY),
      left: originLeft + (event.clientX - startX),
    });
  };

  const handleDragEnd = () => {
    dragRef.current = null;
  };

  const showFieldTag = colnames.length > 1;

  return (
    <div
      role="dialog"
      aria-label={title}
      data-fieldmenu-surface
      className="fixed flex flex-col rounded-lg border border-theme-border-primary bg-theme-bg-secondary shadow-xl"
      style={{ top: position.top, left: position.left, width: PANEL_WIDTH, maxHeight: PANEL_MAX_HEIGHT, zIndex: 100 + z }}
    >
      <div
        className="flex cursor-move items-center justify-between rounded-t-lg border-b border-theme-border-secondary px-3 py-2"
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
      >
        <span className="truncate text-[0.8125rem] font-semibold text-slate-700 dark:text-gray-200">{title}</span>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="ml-2 shrink-0 text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-gray-100"
        >
          ✕
        </button>
      </div>
      <div className="overflow-y-auto px-3 py-2 text-[0.78125rem]">
        {loading && <div className="py-2 text-slate-500 dark:text-gray-400">Loading…</div>}
        {!loading && error && <div className="py-2 text-red-600 dark:text-red-400">{error}</div>}
        {!loading && !error && data && data.count === 0 && (
          <div className="py-2 text-slate-500 dark:text-gray-400">No recorded changes</div>
        )}
        {!loading && !error && data && data.count > 0 && (
          <ul className="divide-y divide-theme-border-secondary">
            {data.records.map((record) => (
              <li key={record.recno} className="py-2">
                {showFieldTag && (
                  <div className="mb-0.5 text-[0.65625rem] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
                    {HISTORY_COLUMN_NAMES[record.colname] ?? record.colname}
                  </div>
                )}
                <div className="text-slate-800 dark:text-gray-100">
                  {record.oldval || '(blank)'} → {record.newval || '(blank)'}
                </div>
                <div
                  className="text-slate-500 dark:text-gray-400"
                  title={new Date(record.datetime).toLocaleString()}
                >
                  {shortUsername(record.userid)} · {formatRelativeDate(record.datetime, new Date())}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
